import PropTypes from 'prop-types'
import { StringDatePickerInput } from '@/components/string-date-picker-input'
import SelectBlock from '../rules-page/select-block'
import { ETDate } from '@/utils/et-date'

const Contract = ({ contract, contractFormik, updateContract, rewardClasses = [] }) => {
  // Format reward classes for dropdown: Description as label, rewardClass as value
  const rewardClassOptions = rewardClasses.map(rc => ({
    label: rc.description || rc.rewardClass,
    value: rc.rewardClass,
  }))

  // Check if dates are in the past (for showing warnings) - only for existing contracts
  const todayETDate = ETDate.today()
  const todayStr = todayETDate.toISO()

  const isExistingContract = contract.savedStartDate && contract.savedStartDate <= todayStr
  const isPastContract = contract.savedEndDate && contract.savedEndDate < todayStr

  const isStartDateEdited = contract.startDate !== contract.savedStartDate
  const isEndDateEdited = contract.endDate !== contract.savedEndDate
  const isRewardClassEdited = contract.rewardClass !== contract.savedRewardClass

  // Warn when start date is set to a past date, or editing when already started
  const showStartDateWarning = isStartDateEdited && (isExistingContract || contract.startDate < todayStr)
  // Warn when changing reward class of a contract that already started
  const showRewardClassWarning = isExistingContract && isRewardClassEdited
  // Warn when end date is set to a past date, or editing when already ended
  const showEndDateWarning = isEndDateEdited && (isPastContract || contract.endDate < todayStr)

  // Warning for editing contract details to rewrite history
  const showWarning = showStartDateWarning || showRewardClassWarning || showEndDateWarning

  // Create a formik-like object for SelectBlock
  const selectBlockFormik = {
    values: { rewardClass: contract.rewardClass },
    errors: {
      rewardClass: contractFormik.errors?.rewardClass,
    },
    touched: {
      rewardClass: true, // Always show validation errors for contract fields
    },
    setFieldValue: (field, value) => {
      updateContract(contract.id, field, value)
    },
    setFieldTouched: () => {
      // No-op since we don't care about touched state
    },
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <StringDatePickerInput
        label="Start Date*"
        placeholder="No start date"
        value={contract.startDate}
        onChange={value => {
          updateContract(contract.id, 'startDate', value)
        }}
        className={showStartDateWarning ? 'border-red-500' : ''}
        helpText={
          contractFormik.errors?.startDate ? (
            <div className="text-red-500 text-sm mt-1.5">{contractFormik.errors.startDate}</div>
          ) : null
        }
      />
      <StringDatePickerInput
        label="End Date"
        placeholder="No end date"
        value={contract.endDate}
        onChange={value => {
          updateContract(contract.id, 'endDate', value)
        }}
        className={showEndDateWarning ? 'border-red-500' : ''}
        helpText={
          contractFormik.errors?.endDate ? (
            <div className="text-red-500 text-sm mt-1.5">{contractFormik.errors.endDate}</div>
          ) : null
        }
      />
      <div className="col-span-2">
        <SelectBlock
          label="Reward Class*"
          id="rewardClass"
          placeholder="Select a reward class"
          formik={selectBlockFormik}
          options={rewardClassOptions}
          showValidation={true}
          className={showRewardClassWarning ? 'border-red-500' : ''}
        />
      </div>
      {showWarning && (
        <div className="col-span-2 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            ⚠️ Editing contract details to rewrite history is not recommended. These changes could affect past
            transactions if they are reprocessed.
          </p>
        </div>
      )}
    </div>
  )
}

Contract.propTypes = {
  contract: PropTypes.object.isRequired,
  contractFormik: PropTypes.object.isRequired,
  updateContract: PropTypes.func.isRequired,
  rewardClasses: PropTypes.array,
}

export default Contract
